/**
 * Bare row keys in the DSL, engine paths in the document.
 *
 * The composer's text and grid views read and write **bare row keys**
 * (`purchasePrice + sdlt`). The stored source document — and therefore the
 * compiler and the ZEN runtime — reads and writes **engine paths**
 * (`inputs.purchasePrice + outputs.sdlt`). This module owns the rewrite in
 * both directions for calc expressions and for outcome tier conditions.
 *
 * The pass is token aware, never string aware only:
 *
 * - text inside a `'…'`, `"…"` or `` `…` `` literal is copied verbatim, so a
 *   key that happens to appear inside a string is never rewritten;
 * - a dotted path is consumed whole, so `blocks.acquisition1.totalPence` and
 *   `listing.marketedPricePence` are left alone;
 * - a member read after a call (`d(x).month`) is left alone, because the
 *   identifier is preceded by `.`;
 * - a call target (`round(`, `sum(`) is left alone, so a row may share a name
 *   with a ZEN function without the call being rewritten;
 * - ZEN's word operators and literals (`and`, `or`, `not`, `in`, `true`,
 *   `false`, `null`) are reserved and never rewritten in either direction.
 *
 * Nothing else about the expression is touched: whitespace, number spelling,
 * parentheses and comments survive byte for byte, which is what lets a real
 * document round-trip through the composer unchanged.
 */

/** The two engine scopes a composer row can occupy. */
export type EngineScope = 'inputs' | 'outputs'

/**
 * Resolve a bare key to the engine scope that holds it, or null when the key
 * names nothing in the model (a dangling reference, a ZEN function, a literal).
 */
export type EngineScopeResolver = (key: string) => EngineScope | null

/**
 * DSL → document: the input key a catalogue fact path binds to — the author's
 * own alias when one exists, otherwise the implicit `sys_…` key. Return null to
 * leave the path exactly as written.
 */
export type FactPathBinder = (factPath: string) => string | null

/**
 * Document → DSL: the catalogue fact path an input key stands for, when that
 * key is an implicit binding rather than a row of the sheet. Return null for
 * every ordinary input.
 */
export type FactPathOfKey = (key: string) => string | null

/** Identifiers ZEN owns; never rewritten, in either direction. */
export const EXPRESSION_RESERVED_WORDS: readonly string[] = [
  'true',
  'false',
  'null',
  'and',
  'or',
  'not',
  'in',
]

const RESERVED = new Set(EXPRESSION_RESERVED_WORDS)

/**
 * The shape of a catalogue fact path — the same pattern the parser uses to
 * recognise a right-hand side that is nothing but a binding. A path of this
 * shape *inside* a larger expression is an IMPLICIT system reference (wave 5b
 * §1): the author never has to alias `listing.marketedPricePence` to a row
 * before using it in a formula.
 */
export const FACT_PATH_IN_EXPRESSION = /^[a-z][A-Za-z0-9]*(?:\.[A-Za-z0-9_]+)+$/u

/**
 * Path roots the ENGINE owns. A dotted path starting with one of these names
 * an engine value (an input, a computed row, a building block instance, a
 * check) rather than a catalogue fact, so it is never turned into a binding.
 */
export const RESERVED_PATH_ROOTS: readonly string[] = [
  'inputs',
  'outputs',
  'blocks',
  'checks',
  'checkDetails',
]

const RESERVED_ROOTS = new Set(RESERVED_PATH_ROOTS)

/**
 * Is this token a catalogue fact path an expression may reference implicitly?
 *
 * `rowKeys` is every name the model declares: a path whose head is one of them
 * is a member read on the author's own row (`holdMonths.value`), not a fact.
 */
export function isBindableFactPath(token: string, rowKeys?: ReadonlySet<string>): boolean {
  if (!FACT_PATH_IN_EXPRESSION.test(token)) return false
  const head = token.slice(0, token.indexOf('.'))
  if (RESERVED_ROOTS.has(head)) return false
  if (RESERVED.has(head)) return false
  return rowKeys === undefined || !rowKeys.has(head)
}

/**
 * Every distinct catalogue fact path referenced inside an expression, in the
 * order it first appears.
 *
 * Token aware exactly as the rewrite is: a path inside a string literal, a
 * member read (`d(x).month`), an engine path (`inputs.…`, `outputs.…`,
 * `blocks.…`) and a call target are all excluded, and so is a path whose head
 * is one of this model's own row keys.
 */
export function factPathsInExpression(expression: string, rowKeys?: Iterable<string>): string[] {
  const keys = rowKeys === undefined
    ? undefined
    : (rowKeys instanceof Set ? (rowKeys as ReadonlySet<string>) : new Set(rowKeys))
  const found: string[] = []
  const seen = new Set<string>()
  mapTokens(expression, (token, previousChar, nextChar) => {
    if (previousChar === '.' || nextChar === '(') return token
    if (!isBindableFactPath(token, keys)) return token
    if (!seen.has(token)) { seen.add(token); found.push(token) }
    return token
  })
  return found
}

const IDENTIFIER_START = /[A-Za-z_$]/u
const IDENTIFIER_PART = /[A-Za-z0-9_$]/u
const DIGIT = /[0-9]/u
const NUMBER_PART = /[0-9._]/u
const EXPONENT = /[eE]/u
const SIGN = /[+-]/u
const WHITESPACE = /\s/u

/** How one token is rendered; the token is returned unchanged when it stays. */
type TokenRewrite = (token: string, previousChar: string, nextChar: string) => string

function scanBack(text: string, index: number): string {
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const char = text[cursor] as string
    if (!WHITESPACE.test(char)) return char
  }
  return ''
}

function scanForward(text: string, index: number): string {
  for (let cursor = index; cursor < text.length; cursor += 1) {
    const char = text[cursor] as string
    if (!WHITESPACE.test(char)) return char
  }
  return ''
}

/** Walk `expression`, handing every identifier or dotted path to `rewrite`. */
function mapTokens(expression: string, rewrite: TokenRewrite): string {
  let out = ''
  let index = 0
  while (index < expression.length) {
    const char = expression[index] as string

    // String literals are opaque.
    if (char === '"' || char === "'" || char === '`') {
      const start = index
      index += 1
      while (index < expression.length) {
        const current = expression[index] as string
        if (current === '\\') { index += 2; continue }
        index += 1
        if (current === char) break
      }
      out += expression.slice(start, index)
      continue
    }

    // Numbers are consumed whole so `1.5` never looks like a path.
    if (DIGIT.test(char)) {
      const start = index
      index += 1
      while (index < expression.length && NUMBER_PART.test(expression[index] as string)) index += 1
      if (index < expression.length && EXPONENT.test(expression[index] as string)) {
        const mark = index
        index += 1
        if (index < expression.length && SIGN.test(expression[index] as string)) index += 1
        if (index < expression.length && DIGIT.test(expression[index] as string)) {
          while (index < expression.length && DIGIT.test(expression[index] as string)) index += 1
        } else index = mark
      }
      out += expression.slice(start, index)
      continue
    }

    if (IDENTIFIER_START.test(char)) {
      const start = index
      index += 1
      while (index < expression.length && IDENTIFIER_PART.test(expression[index] as string)) index += 1
      // Consume the whole dotted path, so a qualified path is one token.
      for (;;) {
        if (expression[index] !== '.') break
        let lookahead = index + 1
        if (lookahead >= expression.length || !IDENTIFIER_START.test(expression[lookahead] as string)) break
        lookahead += 1
        while (lookahead < expression.length && IDENTIFIER_PART.test(expression[lookahead] as string)) lookahead += 1
        index = lookahead
      }
      const token = expression.slice(start, index)
      out += rewrite(token, scanBack(expression, start), scanForward(expression, index))
      continue
    }

    out += char
    index += 1
  }
  return out
}

/**
 * DSL → document. Rewrite every bare key the model knows into its engine path:
 * a system/input row becomes `inputs.<key>`, a `calc` row `outputs.<key>`.
 */
export function expressionToEnginePaths(
  expression: string,
  scopeOf: EngineScopeResolver,
  bindFactPath?: FactPathBinder,
): string {
  return mapTokens(expression, (token, previousChar, nextChar) => {
    if (previousChar === '.') return token          // a member read, not a root
    if (nextChar === '(') return token              // a call target
    if (token.includes('.')) {
      // A catalogue fact referenced straight inside a formula (wave 5b §1).
      // The document has no shape for a bare path, so it binds to an input —
      // the author's own alias when they declared one, otherwise the implicit
      // `sys_…` entry the writer synthesises.
      if (bindFactPath === undefined) return token
      const key = bindFactPath(token)
      return key === null ? token : `inputs.${key}`
    }
    if (RESERVED.has(token)) return token
    const scope = scopeOf(token)
    return scope === null ? token : `${scope}.${token}`
  })
}

/**
 * Document → DSL. Rewrite `inputs.<key>` / `outputs.<key>` back to the bare key
 * — but only when the key really is a row of that kind, so a path the composer
 * does not own survives untouched and re-emits identically.
 */
export function expressionToRowKeys(
  expression: string,
  scopeOf: EngineScopeResolver,
  factPathOfKey?: FactPathOfKey,
): string {
  return mapTokens(expression, (token, previousChar) => {
    if (previousChar === '.') return token
    const separator = token.indexOf('.')
    if (separator < 0) return token
    const scope = token.slice(0, separator)
    if (scope !== 'inputs' && scope !== 'outputs') return token
    const key = token.slice(separator + 1)
    if (key.length === 0 || key.includes('.') || RESERVED.has(key)) return token
    if (scopeOf(key) === scope) return key
    // An implicit binding has no row: it reads back as the fact path itself.
    if (scope === 'inputs' && factPathOfKey !== undefined) {
      const factPath = factPathOfKey(key)
      if (factPath !== null) return factPath
    }
    return token
  })
}

/**
 * Carry a reference across a rename.
 *
 * Renaming a row is not a rename of one cell: every expression and every tier
 * condition that read the old key has to move with it, or the reference is
 * silently orphaned — the document still compiles, because a bare identifier the
 * model does not own is left alone by `expressionToEnginePaths`, and only
 * evaluation discovers that the model cannot run. So the composer rewrites the
 * references rather than blocking the rename.
 *
 * The same token rules as the path rewrite apply: a string literal is opaque, a
 * member read (`x.oldKey`) is not a root reference, a qualified path is one
 * token, a call target is a function rather than a row, and ZEN's own words are
 * never touched. Whitespace and number spelling survive byte for byte.
 */
export function renameExpressionKey(expression: string, from: string, to: string): string {
  if (from === to || from.length === 0 || to.length === 0) return expression
  return mapTokens(expression, (token, previousChar, nextChar) => {
    if (token !== from) return token
    if (previousChar === '.') return token
    if (nextChar === '(') return token
    if (RESERVED.has(token)) return token
    return to
  })
}

/** Build a resolver from `key → scope` pairs; the first binding of a key wins. */
export function engineScopeResolver(bindings: Iterable<readonly [string, EngineScope]>): EngineScopeResolver {
  const scopes = new Map<string, EngineScope>()
  for (const [key, scope] of bindings) if (!scopes.has(key)) scopes.set(key, scope)
  return (key: string): EngineScope | null => scopes.get(key) ?? null
}
